/**
 * use-products — DEC-026 tenant product catalog, read-only client hook.
 *
 * Added for Plan 09.1-11 (job-type creation wizard, Step 1 — "What are you
 * hauling/delivering?"). GET /v1/products (VIEW_RUN_TICKETS-gated) already
 * exists (packages/api/src/routes/products/list.ts); no web-dispatch client
 * hook consumed it before this plan — only `packages/mobile` called it
 * directly via bare `authedFetch`. This hook follows the exact
 * `useApiQuery` pattern already used by `useJobTypes` / `useHaulerOperators`
 * so the wizard's product picker is a normal cached query, not a bespoke
 * fetch.
 *
 * PLATFORM_ADMIN, TENANT_ADMIN and DISPATCH_MANAGER (the three roles that hold
 * MANAGE_REFERENCE_DATA and therefore reach this wizard) all also hold
 * VIEW_RUN_TICKETS
 * (packages/shared/src/auth/permissions.ts), so this call never 403s for a
 * user who can already see the wizard.
 *
 * KNOWN GAP (see 09.1-11-SUMMARY.md "Known Gaps"): the product selected here
 * is used ONLY to derive the wizard's step-7 smart requirement defaults.
 * POST /v1/job-types's locked contract does not accept `defaultProductId`
 * (even though `job_types.default_product_id` exists per DEC-026), so no
 * selection made here is persisted as the created job type's saved product
 * default.
 */

import { useApiQuery, useApiOpts } from '@lodex-pro/web-shared';

/** A row from GET /v1/products (field names match the API response exactly). */
export interface Product {
  readonly id: string;
  readonly name: string;
  readonly code: string;
  readonly unitOfMeasure: string;
}

export interface ProductsResponse {
  readonly products: readonly Product[];
}

export const PRODUCTS_QUERY_KEY = ['products', 'catalog'] as const;

/** Fetch the tenant's active product catalog. staleTime 5 min — near-static,
 *  mirrors useJobTypes' own staleTime for the same reasoning. */
export function useProducts(): ReturnType<typeof useApiQuery<ProductsResponse>> {
  const opts = useApiOpts();
  return useApiQuery<ProductsResponse>(opts, '/v1/products', {
    queryKey: PRODUCTS_QUERY_KEY,
    staleTime: 300_000,
  });
}
